import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Checkbox, Divider, FormControlLabel, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import BadgeDetached from "@entrilia/shared/components/BadgeDetached";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import { getDescriptionByAccess, getLabelByAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import useClientContext from "../../../../../../context/useClientContext";
import { useCopyToEligibleCompanies } from "../hooks/useCopyToEligibleCompanies";
import { CompaniesList } from "./CompanyList";
import SelectCompaniesDialog from "./SelectCompaniesDialog";
import type { SelectedCompanyReport } from "./hooks/SelectedCompany";
import type { ReportValidationType } from "./hooks/useReportValidation";
import { ValidationDetailsContainer } from "./validationDetails/ValidationDetailsContainer";

const accessLevels = [
  ReportAccessType.FullAccess,
  ReportAccessType.Write,
  ReportAccessType.Read,
  ReportAccessType.NoAccess,
];

// Sentinel dropdown value for "inherit from parent" — distinct from the numeric ReportAccessType levels.
const INHERIT_OPTION = "inherit" as const;
const INHERIT_LABEL = "Follow group sharing";
const INHERIT_DESCRIPTION = "Access follows the group";

type AccessOption = ReportAccessType | typeof INHERIT_OPTION;

interface Props {
  validation: ReportValidationType;
  copyConditionValues: boolean;
  copyAccess: ReportAccessType;
  inheritFromParent: boolean;
  onCopyConditionValues: (value: boolean) => void;
  onCopyAccess: (value: ReportAccessType) => void;
  onInheritFromParent: (value: boolean) => void;
}
export default function CopyReportContent({
  validation,
  copyConditionValues,
  copyAccess,
  inheritFromParent,
  onCopyConditionValues,
  onCopyAccess,
  onInheritFromParent,
}: Props) {
  const { clientCode } = useClientContext();
  const eligibleCompanies = useCopyToEligibleCompanies(["ManageReports"]);

  const [showSelectCompany, setShowSelectCompany] = useState(false);
  const [activeCompanyReport, setActiveCompanyReport] = useState<SelectedCompanyReport>();

  const activeCompany = useMemo(() => {
    return validation.selectedCompanies.find((c) => c.code === activeCompanyReport?.companyCode);
  }, [activeCompanyReport, validation.selectedCompanies]);

  const selectedCompanyCodes = useMemo(
    () => validation.selectedCompanies.map((c) => c.code),
    [validation.selectedCompanies]
  );

  const companyList = useMemo(() => {
    return eligibleCompanies
      .filter((c) => c.clientCode !== clientCode)
      .map((c): ListItemData => ({
        value: c.clientCode,
        label: c.title,
        iconSrc: c.branding.logoMarkUrl,
      }));
  }, [clientCode, eligibleCompanies]);

  useEffect(() => {
    if (activeCompanyReport) {
      const report = validation.selectedCompanies
        .find((c) => c.code === activeCompanyReport.companyCode)
        ?.reports.find((r) => r.report.reportId === activeCompanyReport.report.reportId);

      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setActiveCompanyReport(report);
    } else {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setActiveCompanyReport(validation.selectedCompanies[0]?.reports[0]);
    }
  }, [activeCompanyReport, validation.selectedCompanies]);

  return (
    <Grid container sx={{ width: "100%" }}>
      <Stack spacing={2} useFlexGap sx={{ flex: 1, px: 2.5, py: 1.5, width: "100%" }}>
        <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
          <Typography variant="body1">Access level for copied reports</Typography>
          <Select
            size="small"
            sx={{ width: 280 }}
            value={inheritFromParent ? INHERIT_OPTION : copyAccess}
            renderValue={(value) => (
              <Typography>
                {value === INHERIT_OPTION ? INHERIT_LABEL : getLabelByAccess(value as ReportAccessType)}
              </Typography>
            )}
            onChange={(e) => {
              const value = e.target.value as AccessOption;
              if (value === INHERIT_OPTION) {
                onInheritFromParent(true);
                return;
              }
              onInheritFromParent(false);
              onCopyAccess(value);
            }}
          >
            <MenuItem value={INHERIT_OPTION} sx={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
              <Typography>{INHERIT_LABEL}</Typography>
              <Typography variant="caption" sx={{ color: (theme) => `${theme.palette.secondary.main} !important` }}>
                {INHERIT_DESCRIPTION}
              </Typography>
            </MenuItem>
            {accessLevels.map((level) => (
              <MenuItem
                key={level}
                value={level}
                sx={{ display: "flex", flexDirection: "column", alignItems: "start" }}
              >
                <Typography>{getLabelByAccess(level)}</Typography>
                {getDescriptionByAccess(level) && (
                  <Typography variant="caption" sx={{ color: (theme) => `${theme.palette.secondary.main} !important` }}>
                    {getDescriptionByAccess(level)}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <FormControlLabel
          value={copyConditionValues}
          checked={copyConditionValues}
          control={<Checkbox />}
          label="Copy the condition values. If the values do not match or are missing in the target company, they will be omitted."
          onChange={(_, value) => onCopyConditionValues(value)}
        />
        <Typography color={"secondary"}>
          Please provide a list of the companies you wish to copy this report to.
        </Typography>
        <Grid container sx={{ alignItems: "center", gap: 1, width: "100%" }}>
          <Typography variant="subtitle1">Companies</Typography>
          <BadgeDetached color="secondary" badgeContent={validation.selectedCompanies.length} />
          <Button variant="text" startIcon={<AddRoundedIcon />} onClick={() => setShowSelectCompany(true)}>
            Select Companies
          </Button>
        </Grid>
        <CompaniesList
          validation={validation}
          activeCompany={activeCompany}
          activeCompanyReport={activeCompanyReport}
          availableCompanies={companyList}
          onSelectedCompanyReport={setActiveCompanyReport}
        />
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Grid container sx={{ flex: 0.6, width: "100%" }}>
        <ValidationDetailsContainer
          companyReport={activeCompanyReport}
          company={activeCompany}
          updateCompanyReport={validation.updateCompanyReport}
        />
      </Grid>
      <SelectCompaniesDialog
        open={showSelectCompany}
        availableCompanies={companyList}
        selectedCompanies={selectedCompanyCodes}
        handleClose={(companySelection) => {
          validation.updateCompanySelection(companySelection);
          setShowSelectCompany(false);
        }}
      />
    </Grid>
  );
}
